import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';

const PARTICLE_COUNT = 120;
const STRASS_COUNT = 40;

const PALETTE = [
  '#FFD700', // or
  '#FF69B4', // rose
  '#00BFFF', // bleu
  '#7CFC00', // vert
  '#FF4500', // orange
  '#9400D3', // violet
  '#FFFFFF', // blanc
  '#00FFFF', // cyan
  '#FF1493', // deep pink
];

const generateExplosionParticles = () => {
  return Array.from({ length: PARTICLE_COUNT }).map((_, i) => {
    const angle = Math.random() * Math.PI * 2;
    const distance = 120 + Math.random() * 350;
    const tx = Math.cos(angle) * distance;
    const ty = Math.sin(angle) * distance;
    const size = 5 + Math.random() * 10;
    const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
    const delay = Math.random() * 0.25;
    const duration = 0.9 + Math.random() * 1.4;
    const rotate = Math.random() > 0.5 ? 900 : -900;
    return {
      id: `p-${i}`,
      x: 50 + (Math.random() - 0.5) * 20,
      y: 45 + (Math.random() - 0.5) * 20,
      size,
      color,
      delay,
      duration,
      tx,
      ty,
      rotate,
    };
  });
};

const generateStrassParticles = () => {
  return Array.from({ length: STRASS_COUNT }).map((_, i) => ({
    id: `s-${i}`,
    left: Math.random() * 100,
    delay: Math.random() * 2.5,
    duration: 2 + Math.random() * 2,
    size: 4 + Math.random() * 6,
    color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
  }));
};

const WelcomeConfettiEffect = ({ welcomeTitle, welcomeSubtitle }) => {
  const [visible, setVisible] = useState(true);

  const explosion = useMemo(() => generateExplosionParticles(), []);
  const strass = useMemo(() => generateStrassParticles(), []);

  useEffect(() => {
    const t = setTimeout(() => setVisible(false), 15000);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* ── Titre + sous-titre animés ── */}
      {(welcomeTitle || welcomeSubtitle) && (
        <div
          style={{
            position: 'absolute',
            top: '35%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.3, y: 20 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.3, 1.15, 1, 0.9], y: [20, 0, 0, -30] }}
            transition={{ duration: 6, times: [0, 0.1, 0.85, 1], ease: 'easeOut' }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              padding: '28px 36px',
              borderRadius: 20,
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.08)',
            }}
          >
            {welcomeTitle && (
              <div
                style={{
                  fontSize: 'clamp(1.8rem, 5vw, 3.2rem)',
                  fontWeight: 800,
                  color: '#FFFFFF',
                  textShadow: '0 0 20px rgba(255,215,0,0.9), 0 4px 12px rgba(0,0,0,0.6)',
                  lineHeight: 1.2,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  marginBottom: 12,
                }}
              >
                {welcomeTitle}
              </div>
            )}
            {welcomeSubtitle && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: [0, 1, 1, 0], y: [10, 0, 0, -15] }}
                transition={{ duration: 5.5, times: [0, 0.15, 0.85, 1], ease: 'easeOut' }}
                style={{
                  fontSize: 'clamp(1rem, 3vw, 1.5rem)',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  textShadow: '0 0 12px rgba(0,191,255,0.8), 0 2px 8px rgba(0,0,0,0.5)',
                  lineHeight: 1.3,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  maxWidth: 480,
                }}
              >
                {welcomeSubtitle}
              </motion.div>
            )}
          </motion.div>
        </div>
      )}

      {explosion.map((p) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 1, scale: 1, x: 0, y: 0, rotate: 0 }}
          animate={{
            opacity: 0,
            scale: 0.2,
            x: p.tx,
            y: p.ty,
            rotate: p.rotate,
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
          }}
        />
      ))}

      {strass.map((s) => (
        <motion.div
          key={s.id}
          initial={{ y: -20, opacity: 0, rotate: 0 }}
          animate={{ y: '110vh', opacity: [0, 1, 1, 0], rotate: 360 }}
          transition={{
            duration: s.duration,
            delay: s.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{
            position: 'absolute',
            left: `${s.left}%`,
            top: 0,
            width: s.size,
            height: s.size,
            backgroundColor: s.color,
            borderRadius: '50%',
            boxShadow: `0 0 ${s.size * 1.5}px ${s.color}`,
          }}
        />
      ))}
    </div>
  );
};

export default WelcomeConfettiEffect;
